import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  ArchiveBoxIcon,
  ArrowRightStartOnRectangleIcon,
  BugAntIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  PresentationChartLineIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { useEnvironmentFavicon } from '../hooks/useEnvironmentFavicon';
import AuthGuard from '../components/AuthGuard';
import { Avatar } from '../catalyst/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../catalyst/dropdown';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '../catalyst/navbar';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '../catalyst/sidebar';
import { SidebarLayout, useSidebar } from '../catalyst/sidebar-layout';
import { ChevronLeftIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import {
  DocumentDuplicateIcon,
  EyeIcon,
  HomeIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  Square2StackIcon,
  TicketIcon,
} from '@heroicons/react/20/solid';
import Logo from '../assets/brain.svg?react';
import { BeakerIcon, ServerIcon } from '@heroicons/react/24/solid';
import { type ThemeMode, useTheme } from '../hooks/useTheme';
import { GoInput } from '../components/GoInput';
import { ChangelogDialog, useHasNewChangelog } from '../components/ChangelogDialog';
import { GoSearchDialog } from '../components/GoSearchDialog';
import { CurationNav } from './CurationNav';

export function MainLayout() {
  const location = useLocation();
  const { user, logout, server } = useAuth();
  const [showChangelog, setShowChangelog] = useState(false);
  const { hasNew: hasNewChangelog, markSeen: markChangelogSeen } = useHasNewChangelog();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteOpenRef = useRef(paletteOpen);
  useEnvironmentFavicon(server?.googleProjectId);
  useEffect(() => {
    paletteOpenRef.current = paletteOpen;
  }, [paletteOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isOpenPalette =
        (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k';
      if (!isOpenPalette) return;
      if (paletteOpenRef.current) return;
      e.preventDefault();
      setPaletteOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const userDowpdownMenu = (
    <DropdownMenu className="min-w-64" anchor="top start">
      <DarkModeToggle />
      <DropdownDivider />
      <DropdownItem href="/share-feedback">
        <LightBulbIcon />
        <DropdownLabel>Share feedback</DropdownLabel>
      </DropdownItem>
      <DropdownDivider />
      <DropdownItem href="/security">
        <ShieldCheckIcon />
        <DropdownLabel>Security</DropdownLabel>
      </DropdownItem>
      <DropdownDivider />
      <DropdownItem onClick={handleLogout}>
        <ArrowRightStartOnRectangleIcon />
        <DropdownLabel>Sign out</DropdownLabel>
      </DropdownItem>
    </DropdownMenu>
  );

  return (
    <>
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection>
            <NavbarItem href="/search" aria-label="Search">
              <MagnifyingGlassIcon />
            </NavbarItem>
            <NavbarItem href="/inbox" aria-label="Inbox">
              <InboxIcon />
            </NavbarItem>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar
                  // src="/profile-photo.jpg"
                  initials={user?.name
                    ?.split(' ')
                    ?.map((n) => n[0])
                    ?.join('')}
                  className="size-10"
                  square
                  alt=""
                />
              </DropdownButton>
              {userDowpdownMenu}
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarHeader>
            <div className="flex items-center gap-1 lg:group-data-[collapsed=true]/sidebar:flex-col lg:group-data-[collapsed=true]/sidebar:gap-2 lg:group-data-[collapsed=true]/sidebar:items-center">
              <div className="min-w-0">
                <Dropdown>
                  <DropdownButton as={SidebarItem} className="lg:mb-2.5" tooltip="TRecs — environment menu">
                    <Logo className="size-6" />
                    <SidebarLabel>
                      TRecs <WhereAMI />
                    </SidebarLabel>
                    <ShouldShowEnvironemntChooser>
                      <span className="lg:group-data-[collapsed=true]/sidebar:hidden">
                        <ChevronDownIcon />
                      </span>
                    </ShouldShowEnvironemntChooser>
                  </DropdownButton>
                  <ShouldShowEnvironemntChooser>
                    <DropdownMenu className="min-w-80 lg:min-w-64" anchor="bottom start">
                      <EnvironmentChooserItems />
                      <DropdownDivider />
                      <ServerInfoFooter />
                    </DropdownMenu>
                  </ShouldShowEnvironemntChooser>
                </Dropdown>
              </div>
              <CollapseToggleButton />
            </div>
          </SidebarHeader>
          <SidebarBody className="[&>[data-slot=section]+[data-slot=section]]:!mt-3">
            <SidebarSection className="lg:group-data-[collapsed=true]/sidebar:hidden">
              <GoInput />
            </SidebarSection>
            <SidebarSection>
              <SidebarHeading>Workspace</SidebarHeading>
              <AuthGuard roles={['admin', 'user']}>
                <SidebarItem
                  hidesSidebarOnClick={true}
                  to="/"
                  as={Link}
                  current={isActive('/')}
                  tooltip="Home"
                >
                  <HomeIcon />
                  <SidebarLabel>Home</SidebarLabel>
                </SidebarItem>
              </AuthGuard>
              <SidebarItem hidesSidebarOnClick={true} to="/documents" as={Link} current={isActive('/documents')} tooltip="Documents">
                <Square2StackIcon />
                <SidebarLabel>Documents</SidebarLabel>
              </SidebarItem>
              <AuthGuard roles={['admin', 'user', 'supervisor', 'curator']}>
                <SidebarItem
                  hidesSidebarOnClick={true}
                  to="/document-pages"
                  as={Link}
                  current={isActive('/document-pages')}
                  tooltip="Pages"
                >
                  <TicketIcon />
                  <SidebarLabel>Pages</SidebarLabel>
                </SidebarItem>
              </AuthGuard>
              <AuthGuard roles={['admin', 'user', 'supervisor', 'curator']}>
                <SidebarItem
                  hidesSidebarOnClick={true}
                  to="/reconstructed-documents"
                  as={Link}
                  current={isActive('/reconstructed-documents')}
                  tooltip="Reconstructed"
                >
                  <DocumentDuplicateIcon />
                  <SidebarLabel>Reconstructed</SidebarLabel>
                </SidebarItem>
              </AuthGuard>
              <AuthGuard roles={['admin']}>
                <SidebarItem hidesSidebarOnClick={true} to="/uploads" as={Link} current={isActive('/uploads')} tooltip="Uploads">
                  <CloudArrowUpIcon />
                  <SidebarLabel>Uploads</SidebarLabel>
                </SidebarItem>
              </AuthGuard>
              <AuthGuard roles={['admin', 'user', 'curator']}>
                <SidebarItem hidesSidebarOnClick={true} to="/projects" as={Link} current={isActive('/projects')} tooltip="Projects">
                  <ArchiveBoxIcon />
                  <SidebarLabel>Projects</SidebarLabel>
                </SidebarItem>
              </AuthGuard>
              <AuthGuard roles={['admin', 'user', 'curator']}>
                <SidebarItem
                  hidesSidebarOnClick={true}
                  to="/organisations"
                  as={Link}
                  current={isActive('/organisations')}
                  tooltip="Organisations"
                >
                  <BuildingOffice2Icon className="size-5" />
                  <SidebarLabel>Organisations</SidebarLabel>
                </SidebarItem>
              </AuthGuard>
            </SidebarSection>
            <AuthGuard roles={['admin', 'supervisor', 'curator']}>
              <SidebarSection>
                <SidebarHeading>Admin</SidebarHeading>
                <AuthGuard roles={['admin', 'supervisor', 'curator']}>
                  <SidebarItem hidesSidebarOnClick={true} to="/categories" as={Link} current={isActive('/categories')} tooltip="Categories">
                    <BeakerIcon />
                    <SidebarLabel>Categories</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
                <AuthGuard roles={['admin', 'supervisor', 'curator']}>
                  <CurationNav />
                </AuthGuard>
                <AuthGuard roles={['admin', 'supervisor']}>
                  <SidebarItem hidesSidebarOnClick={true} to="/supertypes" as={Link} current={isActive('/supertypes')} tooltip="Supertypes">
                    <PuzzlePieceIcon />
                    <SidebarLabel>Supertypes</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
                <AuthGuard roles={['admin', 'supervisor', 'curator']}>
                  <SidebarItem
                    hidesSidebarOnClick={true}
                    to="/supervisor/audit"
                    as={Link}
                    current={isActive('/supervisor/audit')}
                    tooltip="Supervisor audit"
                  >
                    <ClipboardDocumentCheckIcon />
                    <SidebarLabel>Supervisor audit</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
                <AuthGuard roles={['admin']}>
                  <SidebarItem hidesSidebarOnClick={true} to="/users" as={Link} current={isActive('/users')} tooltip="Users">
                    <UsersIcon />
                    <SidebarLabel>Users</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
                <AuthGuard roles={['admin']}>
                  <SidebarItem
                    hidesSidebarOnClick={true}
                    to="/undelivered-events"
                    as={Link}
                    current={isActive('/undelivered-events')}
                    tooltip="Undelivered to Oil"
                  >
                    <ExclamationTriangleIcon />
                    <SidebarLabel>Undelivered to Oil</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
                <AuthGuard roles={['admin']}>
                  <SidebarItem
                    hidesSidebarOnClick={true}
                    to="/chat-questions"
                    as={Link}
                    current={isActive('/chat-questions')}
                    tooltip="Chat questions"
                  >
                    <ChatBubbleLeftRightIcon />
                    <SidebarLabel>Chat questions</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
              </SidebarSection>
            </AuthGuard>
            <AuthGuard roles={['admin', 'user', 'curator']}>
              <SidebarSection>
                <SidebarHeading>System</SidebarHeading>
                <AuthGuard roles={['admin']}>
                  <SidebarItem hidesSidebarOnClick={true} to="/audit" as={Link} current={isActive('/audit')} tooltip="Audit">
                    <EyeIcon />
                    <SidebarLabel>Audit</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
                <SidebarItem hidesSidebarOnClick={true} to="/debugger" as={Link} current={isActive('/debugger')} tooltip="Debugger">
                  <BugAntIcon />
                  <SidebarLabel>Debugger</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  hidesSidebarOnClick={true}
                  to="/classification-quality"
                  as={Link}
                  current={isActive('/classification-quality')}
                  tooltip="Classification quality"
                >
                  <PresentationChartLineIcon data-slot="icon" />
                  <SidebarLabel>Classification quality</SidebarLabel>
                </SidebarItem>
                <SidebarItem hidesSidebarOnClick={true} to="/status" as={Link} current={isActive('/status')} tooltip="Server Status">
                  <ServerIcon />
                  <SidebarLabel>Server Status</SidebarLabel>
                </SidebarItem>
                <AuthGuard roles={['admin', 'curator']}>
                  <SidebarItem hidesSidebarOnClick={true} to="/monitoring" as={Link} current={isActive('/monitoring')} tooltip="Pipeline monitoring">
                    <ChartBarIcon />
                    <SidebarLabel>Monitoring</SidebarLabel>
                  </SidebarItem>
                </AuthGuard>
              </SidebarSection>
            </AuthGuard>
            <SidebarSpacer />
            <SidebarSection>
              <div className="relative">
                <SidebarItem
                  onClick={() => {
                    markChangelogSeen();
                    setShowChangelog(true);
                  }}
                  tooltip="Changelog"
                >
                  <SparklesIcon />
                  <SidebarLabel>
                    Changelog
                    {hasNewChangelog && <span className="ml-2 inline-block size-2 rounded-full bg-blue-500 dark:bg-blue-400" />}
                  </SidebarLabel>
                </SidebarItem>
                {hasNewChangelog && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1.5 right-1.5 hidden size-2 rounded-full bg-blue-500 dark:bg-blue-400 lg:group-data-[collapsed=true]/sidebar:block"
                  />
                )}
              </div>
              <ChangelogDialog open={showChangelog} onClose={() => setShowChangelog(false)} />
            </SidebarSection>
          </SidebarBody>
          <SidebarFooter className="max-lg:hidden lg:group-data-[collapsed=true]/sidebar:px-2">
            <Dropdown>
              <DropdownButton
                as={SidebarItem}
                tooltip={
                  user?.name
                    ? user.role
                      ? `${user.name} (${user.role}) — open menu`
                      : `${user.name} — open menu`
                    : 'Open user menu'
                }
              >
                <span className="flex min-w-0 items-center gap-3 lg:group-data-[collapsed=true]/sidebar:mx-auto lg:group-data-[collapsed=true]/sidebar:gap-0">
                  <Avatar
                    initials={user?.name
                      ?.split(' ')
                      ?.map((n) => n[0])
                      ?.join('')}
                    className="size-10 lg:group-data-[collapsed=true]/sidebar:size-8"
                    square
                    alt=""
                  />
                  <span className="min-w-0 lg:group-data-[collapsed=true]/sidebar:hidden">
                    <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-zinc-100">
                      {user?.name}
                    </span>
                    <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                      {user?.role}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon className="lg:group-data-[collapsed=true]/sidebar:hidden" />
              </DropdownButton>
              {userDowpdownMenu}
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
    >
      <Outlet />
    </SidebarLayout>
    <GoSearchDialog open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}

function DarkModeToggle() {
  const { setMode, mode } = useTheme();
  const modes: ThemeMode[] = ['light', 'dark', 'system'];

  return (
    <div className="col-span-full px-3.5 py-2.5 sm:px-3 sm:py-1.5">
      <div className="flex items-center gap-2 ml-2">
        {modes.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-2 py-1 rounded text-xs border transition-colors cursor-pointer
            ${
              mode === m
                ? 'bg-zinc-800 text-white border-zinc-800 dark:bg-zinc-200 dark:text-zinc-900 dark:border-zinc-200'
                : 'text-zinc-700 bg-white dark:bg-zinc-800 dark:text-zinc-200 border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700'
            }
          `}
            aria-pressed={mode === m}
          >
            {m === 'light' ? '☀️' : m === 'dark' ? '🌙' : '🌤️'}
            <span className="ml-1 capitalize">{m}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function WhereAMI() {
  const { server } = useAuth();
  const tooltip = [
    server?.googleProjectId && `Project: ${server.googleProjectId}`,
    server?.hostname && `Host: ${server.hostname}`,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span title={tooltip || undefined}>
      <LocalhostBadge />
      <EnvironmentBadge />
    </span>
  );
}

function EnvironmentChooserItems() {
  const { server } = useAuth();

  return (
    <>
      <DropdownItem
        href="https://trecs-brain-1049481196174.europe-central2.run.app"
        current={server?.googleProjectId === 'trecs-aero-prod'}
      >
        <Avatar slot="icon" initials="PR" className="bg-red-500 text-white" />
        <DropdownLabel>Production</DropdownLabel>
      </DropdownItem>
      <DropdownItem href="https://brain.sandbox.trecs.dev" current={server?.googleProjectId === 'trecs-aero-sandbox'}>
        <Avatar slot="icon" initials="SB" className="bg-amber-500 text-white" />
        <DropdownLabel>Sandbox</DropdownLabel>
      </DropdownItem>
      <DropdownItem href="https://brain.dev.trecs.dev" current={server?.googleProjectId === 'trecs-aero-dev'}>
        <Avatar slot="icon" initials="DEV" className="bg-green-500 text-white" />
        <DropdownLabel>Development</DropdownLabel>
      </DropdownItem>
    </>
  );
}

function ServerInfoFooter() {
  const { server } = useAuth();

  return (
    <>
      <div />
      <div className="px-3.5 py-2 text-xs text-zinc-500 dark:text-zinc-400 space-y-0.5">
        <div className="font-mono truncate" title={server?.googleProjectId ?? undefined}>
          Project: {server?.googleProjectId ?? '—'}
        </div>
        <div className="font-mono truncate" title={server?.hostname}>
          Host: {server?.hostname ?? '—'}
        </div>
      </div>
    </>
  );
}

function EnvironmentBadge() {
  const { server } = useAuth();
  const projectId = server?.googleProjectId;

  if (!projectId) {
    return null;
  }

  if (/prod/i.test(projectId)) {
    return <span className={'text-red-500'}>PROD</span>;
  }

  if (/sandbox/i.test(projectId)) {
    return <span className={'text-amber-500'}>SANDBOX</span>;
  }

  if (/dev/i.test(projectId)) {
    return <span className={'text-green-500'}>DEV</span>;
  }

  return <span className={'text-amber-500'}>{projectId}</span>;
}

function LocalhostBadge() {
  const isLocal = location.hostname.match(/localhost|127\.0\.0\.1/);

  return isLocal ? <span className={'mr-1 text-blue-500'}>LOCAL</span> : null;
}

function ShouldShowEnvironemntChooser({ children }: { children: React.ReactNode }) {
  const { server } = useAuth();
  const isLocal = location.hostname.match(/localhost|127\.0\.0\.1/);

  if (isLocal) {
    return children;
  }

  if (server?.environment === 'production' || server?.environment === 'develop' || server?.environment === 'sandbox') {
    return children;
  }

  return null;
}

function CollapseToggleButton() {
  const { collapsed, toggleCollapsed } = useSidebar();
  return (
    <button
      type="button"
      onClick={toggleCollapsed}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      title={collapsed ? 'Expand sidebar (⌘/Ctrl+B)' : 'Collapse sidebar (⌘/Ctrl+B)'}
      className="rounded p-1 text-zinc-500 hover:bg-zinc-950/5 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white max-lg:hidden"
    >
      <ChevronLeftIcon
        className={clsx('size-4 transition-transform duration-200', collapsed && 'rotate-180')}
      />
    </button>
  );
}
